import React, {
  Children,
  Fragment,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { PlayerResult } from '@shared/api-dto';

import './marquee.css';

const INIT_DELAY = 50;
const ANIMATION_DELAY = 220;

export type MarqueeVariant = 'neutral' | 'team1' | 'team2' | 'tie';

type MarqueeItemProps = {
  duration: number;
  variant?: MarqueeVariant;
  /** Drop the dim/blur overlay so the board (team scores) stays readable. */
  clearBackdrop?: boolean;
  /** No card or overlay — just wait, so the scoreboard can sit unobstructed. */
  hidden?: boolean;
  children?: ReactNode;
}

export const MarqueeItem = ({ children }: MarqueeItemProps) => {
  return <>{children}</>;
};

type MarqueePlayerProps = {
  team?: number;
  className?: string;
  children: ReactNode;
};

/** Player name in marquee copy: bigger, team-colored, with a glow that reads from across the room. */
export const MarqueePlayer = ({ team, className, children }: MarqueePlayerProps) => {
  const teamClass = team === 1 ? 'team1' : team === 2 ? 'team2' : '';
  return (
    <span className={['marqueePlayer', teamClass, className].filter(Boolean).join(' ')}>
      {children}
    </span>
  );
};

/** "Alice and Bob" / "Alice, Bob and Carol" with each name flaired. */
export const joinMarqueePlayers = (players: PlayerResult[]): ReactNode => {
  if (players.length === 0) {
    return null;
  }
  return players.map((p, i) => {
    const sep = i === 0 ? null : i === players.length - 1 ? ' and ' : ', ';
    return (
      <Fragment key={p.user.guid}>
        {sep}
        <MarqueePlayer team={p.team}>{p.user.name}</MarqueePlayer>
      </Fragment>
    );
  });
};

type WrappedItemProps = MarqueeItemProps & {
  onComplete: () => void;
}

const WrappedItem = ({ onComplete, children, duration, variant = 'neutral', clearBackdrop, hidden }: WrappedItemProps) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    if (hidden) {
      const timeout = setTimeout(onComplete, duration);
      return () => clearTimeout(timeout);
    }

    let timeout: NodeJS.Timeout;
    const initTimeout = setTimeout(() => {
      setIsActive(true);
      timeout = setTimeout(() => {
        setIsActive(false);
        setTimeout(onComplete, ANIMATION_DELAY);
      }, Math.max(duration - INIT_DELAY, 0));
    }, INIT_DELAY);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(timeout);
    };
  }, []);

  if (hidden) {
    return null;
  }

  const className = [
    'marqueeItem',
    variant !== 'neutral' ? variant : '',
    clearBackdrop ? 'clearBackdrop' : '',
    isActive ? 'active' : ''
  ].filter(Boolean).join(' ');

  return <div className={className}>
    <div className='marqueeInner'>
      {children}
    </div>
  </div>;
};

const isMarqueeItem = (element: ReactNode): element is ReactElement<MarqueeItemProps> => {
  return isValidElement(element) && element.type === MarqueeItem;
};

/*
const isFragment = (element: ReactNode): element is ReactElement => {
  return isValidElement(element) && element.type === React.Fragment;
};
*/

const isFragment = (element: ReactNode): element is ReactElement & { props: { children: ReactNode } } => {
  return isValidElement(element) && element.type === React.Fragment && (element.props as { children?: ReactNode }).children !== undefined;
};

type MarqueeProps = {
  onComplete: () => void;
  children: ReactNode;
};

export const Marquee = ({ onComplete, children }: MarqueeProps) => {
  const [curItem, setCurItem] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Keep `active` in React state. classList.add('active') was wiped every
  // time the current slide changed, so later cards (e.g. the Read the Room
  // guess) floated over the board with no dimmed backdrop.
  useEffect(() => {
    const timeout = setTimeout(() => setIsActive(true), 0);
    return () => clearTimeout(timeout);
  }, []);

  const wrappedChildren = useMemo(() => {
    const onItemComplete = (i: number) => {
      setCurItem(i + 1);
    };

    let i = 0;
    const flatten = (children: ReactNode) => {
      const acc: ReactNode[] = [];
      Children.forEach(children, (c) => {
        if (isMarqueeItem(c)) {
          acc.push(
            <WrappedItem key={`wrapped-marquee-${i}`} {...(c.props)} onComplete={onItemComplete.bind(null, i)}>
              {c.props.children}
            </WrappedItem>
          );
          ++i;
        } else if (isFragment(c)) {
          acc.push(...flatten(c.props.children));
        }
      });
      return acc;
    };

    return flatten(children);
  }, [children]);

  useEffect(() => {
    if (!wrappedChildren || curItem !== wrappedChildren.length) {
      return;
    }
    setIsActive(false);
    const timeout = setTimeout(() => onCompleteRef.current(), ANIMATION_DELAY);
    return () => clearTimeout(timeout);
  }, [curItem, wrappedChildren]);

  if (!wrappedChildren) {
    return null;
  }

  const current = wrappedChildren[curItem];
  if (!current) {
    return null;
  }

  const currentProps = isValidElement<WrappedItemProps>(current)
    ? current.props
    : undefined;
  const hidden = Boolean(currentProps?.hidden);

  // Hidden beats unmount the overlay entirely so the scoreboard is the
  // only thing on screen.
  if (hidden) {
    return <>{current}</>;
  }

  const clearBackdrop = Boolean(currentProps?.clearBackdrop);

  return (
    <div className={
      'marqueeContainer' +
      (isActive ? ' active' : '') +
      (clearBackdrop ? ' clearBackdrop' : '')
    }>
      {current}
    </div>
  );
};
